import { motion } from "framer-motion";
import { Coins } from "lucide-react";

interface CoinParticleProps {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  delay: number;
  onComplete: () => void;
}

const SPARKLES = [
  { angle: 0,   dist: 18, color: "#facc15", size: 5 },
  { angle: 60,  dist: 22, color: "#fb923c", size: 4 },
  { angle: 120, dist: 18, color: "#f472b6", size: 5 },
  { angle: 180, dist: 22, color: "#34d399", size: 4 },
  { angle: 240, dist: 18, color: "#60a5fa", size: 5 },
  { angle: 300, dist: 22, color: "#a78bfa", size: 4 },
];

function SparkleParticle({ angle, dist, color, size, delay }: { angle: number; dist: number; color: string; size: number; delay: number }) {
  const rad = (angle * Math.PI) / 180;
  const tx = Math.cos(rad) * dist;
  const ty = Math.sin(rad) * dist;

  return (
    <motion.div
      className="absolute rounded-full pointer-events-none"
      style={{
        width: size,
        height: size,
        backgroundColor: color,
        top: "50%",
        left: "50%",
        marginTop: -size / 2,
        marginLeft: -size / 2,
        boxShadow: `0 0 6px 2px ${color}88`,
      }}
      initial={{ x: 0, y: 0, scale: 0, opacity: 0 }}
      animate={{
        x: [0, tx * 0.5, tx],
        y: [0, ty * 0.5, ty],
        scale: [0, 1.4, 0],
        opacity: [0, 1, 0],
      }}
      transition={{
        duration: 0.55,
        delay: delay + 0.05,
        ease: "easeOut",
        times: [0, 0.4, 1],
      }}
    />
  );
}

function StarParticle({ angle, dist, color, delay }: { angle: number; dist: number; color: string; delay: number }) {
  const rad = (angle * Math.PI) / 180;
  const tx = Math.cos(rad) * dist * 1.6;
  const ty = Math.sin(rad) * dist * 1.6;

  return (
    <motion.div
      className="absolute pointer-events-none text-[8px] font-bold select-none"
      style={{ top: "50%", left: "50%", color, lineHeight: 1 }}
      initial={{ x: 0, y: 0, scale: 0, opacity: 0 }}
      animate={{
        x: [0, tx],
        y: [0, ty],
        scale: [0, 1.2, 0],
        opacity: [0, 1, 0],
        rotate: [0, 360],
      }}
      transition={{
        duration: 0.65,
        delay: delay + 0.08,
        ease: "easeOut",
      }}
    >
      ✦
    </motion.div>
  );
}

export function CoinParticle({ startX, startY, endX, endY, delay, onComplete }: CoinParticleProps) {
  // Random lateral wobble per coin so they fan out
  const wobbleX = (Math.random() - 0.5) * 80;
  const controlX = startX + (endX - startX) * 0.5 + wobbleX;
  const controlY = Math.min(startY, endY) - 120;

  return (
    <motion.div
      className="fixed top-0 left-0 pointer-events-none z-[99999]"
      initial={{
        x: startX - 20,
        y: startY - 20,
        scale: 0,
        opacity: 0,
        rotate: 0,
      }}
      animate={{
        x: [startX - 20, controlX - 20, endX - 20],
        y: [startY - 20, controlY - 20, endY - 20],
        scale: [0, 1.3, 1.1, 0.9, 0],
        opacity: [0, 1, 1, 1, 0],
        rotate: [0, 180, 360, 540],
      }}
      transition={{
        duration: 1.1,
        delay,
        times: [0, 0.15, 0.5, 0.85, 1],
        ease: [0.34, 1.56, 0.64, 1],
      }}
      onAnimationComplete={onComplete}
    >
      <div className="relative w-10 h-10">
        {/* Glow halo */}
        <motion.div
          className="absolute inset-[-6px] rounded-full bg-gradient-to-br from-yellow-400 via-amber-400 to-orange-400 blur-lg opacity-70"
          animate={{
            scale: [1, 1.6, 1],
            opacity: [0.7, 1, 0.7],
          }}
          transition={{ duration: 0.5, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Sparkle dots burst */}
        {SPARKLES.map((s, i) => (
          <SparkleParticle key={i} {...s} delay={delay} />
        ))}

        {/* Star chars */}
        {[30, 90, 150, 210, 270, 330].map((angle, i) => (
          <StarParticle
            key={i}
            angle={angle}
            dist={20}
            color={["#facc15", "#fb923c", "#f472b6", "#34d399", "#60a5fa", "#a78bfa"][i]}
            delay={delay}
          />
        ))}

        {/* Coin body */}
        <div className="relative w-10 h-10 rounded-full bg-gradient-to-br from-yellow-200 via-yellow-400 to-amber-600 flex items-center justify-center shadow-[0_0_25px_rgba(250,204,21,0.9),0_0_45px_rgba(251,191,36,0.5)] border-2 border-yellow-100 overflow-hidden z-10">
          {/* Shimmer */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent"
            animate={{ x: [-40, 40] }}
            transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
          />
          <Coins className="w-5 h-5 text-amber-900 relative z-10" />
        </div>
      </div>
    </motion.div>
  );
}
